import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, Paper,
  IconButton, Stack, Avatar, Divider, Tabs, Tab, Alert
} from '@mui/material';
import {
  ArrowBack, Add, Edit, Delete, Inventory, Warehouse, Search, FilterList,
  Chair, Build, DirectionsCar, Computer, Hardware, Description, Assignment
} from '@mui/icons-material';
import { warehouseAssetsAPI, inventoryAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';

const ASSET_TYPES = [
  { value: 'furniture', label: 'Furniture', icon: <Chair /> },
  { value: 'equipment', label: 'Equipment', icon: <Build /> },
  { value: 'vehicle', label: 'Vehicle', icon: <DirectionsCar /> },
  { value: 'electronics', label: 'Electronics', icon: <Computer /> },
  { value: 'tools', label: 'Tools', icon: <Hardware /> },
  { value: 'office_supplies', label: 'Office Supplies', icon: <Description /> },
  { value: 'other', label: 'Other', icon: <Assignment /> },
];

const CONDITION_OPTIONS = ['new', 'excellent', 'good', 'fair', 'poor', 'damaged', 'broken'];
const STATUS_OPTIONS = ['in_storage', 'in_use', 'maintenance', 'retired', 'lost'];

const AssetForm = ({ open, onClose, asset, warehouseId, onSaved }) => {
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (asset) {
        reset({
          ...asset,
          purchase_date: asset.purchase_date ? asset.purchase_date.split('T')[0] : '',
          warranty_expiry: asset.warranty_expiry ? asset.warranty_expiry.split('T')[0] : '',
          last_maintenance_date: asset.last_maintenance_date ? asset.last_maintenance_date.split('T')[0] : '',
          next_maintenance_date: asset.next_maintenance_date ? asset.next_maintenance_date.split('T')[0] : '',
        });
      } else {
        reset({
          warehouse_id: warehouseId,
          asset_name: '',
          asset_type: 'furniture',
          description: '',
          serial_number: '',
          model: '',
          brand: '',
          purchase_date: '',
          purchase_cost: '',
          current_value: '',
          condition: 'good',
          status: 'in_storage',
          location_within_warehouse: '',
          assigned_to: '',
          warranty_expiry: '',
          last_maintenance_date: '',
          next_maintenance_date: '',
          notes: '',
        });
      }
    }
  }, [open, asset, warehouseId, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const payload = {
        ...data,
        purchase_cost: data.purchase_cost ? parseFloat(data.purchase_cost) : null,
        current_value: data.current_value ? parseFloat(data.current_value) : null,
      };
      
      if (asset?.id) {
        await warehouseAssetsAPI.update(asset.id, payload);
        toast.success('Asset updated successfully');
      } else {
        await warehouseAssetsAPI.create(payload);
        toast.success('Asset created successfully');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save asset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>{asset?.id ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Asset Name *" {...register('asset_name', { required: 'Asset name required' })} error={!!errors.asset_name} helperText={errors.asset_name?.message} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Asset Type *</InputLabel>
                <Controller
                  name="asset_type"
                  control={control}
                  rules={{ required: 'Asset type required' }}
                  render={({ field }) => (
                    <Select {...field} label="Asset Type *">
                      <MenuItem value="">Select type</MenuItem>
                      {ASSET_TYPES.map(type => (
                        <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={2} {...register('description')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Serial Number" {...register('serial_number')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Model" {...register('model')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Brand" {...register('brand')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Purchase Date" type="date" {...register('purchase_date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Purchase Cost" type="number" {...register('purchase_cost')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Current Value" type="number" {...register('current_value')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Condition</InputLabel>
                <Controller
                  name="condition"
                  control={control}
                  defaultValue="good"
                  render={({ field }) => (
                    <Select {...field} label="Condition">
                      {CONDITION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>)}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Controller
                  name="status"
                  control={control}
                  defaultValue="in_storage"
                  render={({ field }) => (
                    <Select {...field} label="Status">
                      {STATUS_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt.replace('_', ' ').toUpperCase()}</MenuItem>)}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Location in Warehouse" {...register('location_within_warehouse')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Warranty Expiry" type="date" {...register('warranty_expiry')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Last Maintenance" type="date" {...register('last_maintenance_date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Next Maintenance" type="date" {...register('next_maintenance_date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2} {...register('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (asset?.id ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const WarehouseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [warehouse, setWarehouse] = useState(null);
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchWarehouse = useCallback(async () => {
    try {
      const { data } = await inventoryAPI.getWarehouses();
      const warehouseData = data.data.find(w => w.id === id);
      setWarehouse(warehouseData);
    } catch (err) {
      toast.error('Failed to load warehouse');
    }
  }, [id]);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const params = { warehouse_id: id };
      if (filterType) params.asset_type = filterType;
      if (filterStatus) params.status = filterStatus;
      
      const { data } = await warehouseAssetsAPI.getAll(params);
      setAssets(data.data || []);
    } catch (err) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [id, filterType, filterStatus]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await warehouseAssetsAPI.getSummary({ warehouse_id: id });
      setSummary(data.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchWarehouse();
    fetchAssets();
    fetchSummary();
  }, [fetchWarehouse, fetchAssets, fetchSummary]);

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    
    try {
      await warehouseAssetsAPI.delete(assetId);
      toast.success('Asset deleted successfully');
      fetchAssets();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete asset');
    }
  };

  const handleFormSaved = () => {
    fetchAssets();
    fetchSummary();
  };

  const filteredAssets = assets;

  if (loading && !warehouse) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/warehouses')}>
          Back
        </Button>
        <Typography variant="h4" fontWeight={700}>
          {warehouse?.name || 'Warehouse'}
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab label="Overview" icon={<Warehouse />} iconPosition="start" />
        <Tab label="Assets" icon={<Inventory />} iconPosition="start" />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">Total Assets</Typography>
                <Typography variant="h4" fontWeight={700} color="primary">{summary?.total_assets || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">Total Value</Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  ${Number(summary?.total_current_value || 0).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">In Storage</Typography>
                <Typography variant="h4" fontWeight={700} color="info.main">{summary?.in_storage_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">In Use</Typography>
                <Typography variant="h4" fontWeight={700} color="warning.main">{summary?.in_use_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {ASSET_TYPES.map(type => (
            <Grid item xs={12} sm={6} md={4} key={type.value}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>{type.icon}</Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">{type.label}</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {summary?.[`${type.value}_count`] || 0}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter by Type</InputLabel>
                <Select value={filterType} label="Filter by Type" onChange={(e) => setFilterType(e.target.value)}>
                  <MenuItem value="">All Types</MenuItem>
                  {ASSET_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select value={filterStatus} label="Filter by Status" onChange={(e) => setFilterStatus(e.target.value)}>
                  <MenuItem value="">All Status</MenuItem>
                  {STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt.replace('_', ' ').toUpperCase()}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" startIcon={<Add />} onClick={() => { setSelectedAsset(null); setFormOpen(true); }}>
                Add Asset
              </Button>
            </Stack>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredAssets.length === 0 ? (
            <Alert severity="info">No assets found. Add your first asset to get started.</Alert>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAssets.map(asset => (
                  <TableRow key={asset.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{asset.asset_name}</Typography>
                      {asset.serial_number && (
                        <Typography variant="caption" color="text.secondary">SN: {asset.serial_number}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={asset.asset_type.replace('_', ' ')} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={asset.condition} 
                        size="small" 
                        color={asset.condition === 'new' || asset.condition === 'excellent' || asset.condition === 'good' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={asset.status.replace('_', ' ')} 
                        size="small" 
                        color={asset.status === 'in_use' ? 'warning' : asset.status === 'maintenance' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{asset.location_within_warehouse || '-'}</TableCell>
                    <TableCell>${Number(asset.current_value || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => { setSelectedAsset(asset); setFormOpen(true); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(asset.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      <AssetForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        asset={selectedAsset}
        warehouseId={id}
        onSaved={handleFormSaved}
      />
    </Box>
  );
};

export default WarehouseDetailPage;
